import { fetchStrapi } from './strapi';

export type FaqCategory =
  | 'coaching'
  | 'kamingespraeche'
  | 'bhs-modell'
  | 'ablauf'
  | 'investition'
  | 'sonstiges';

export interface Faq {
  id: number;
  question: string;
  answer: string;
  category: FaqCategory;
  homeOrder: number;
}

export interface FaqSeite {
  badgeText: string;
  heading: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  pageTitle: string;
  pageSubheading: string;
  seoDescription: string;
  noEntriesText: string;
}

export const FAQ_CATEGORY_LABELS: Record<FaqCategory, string> = {
  coaching: 'Coaching',
  kamingespraeche: 'Kamingespräche',
  'bhs-modell': 'BHS-Modell',
  ablauf: 'Ablauf',
  investition: 'Investition',
  sonstiges: 'Sonstiges',
};

// Order in which categories should appear on the FAQ page (groups follow this order)
export const FAQ_CATEGORY_ORDER: FaqCategory[] = [
  'coaching',
  'kamingespraeche',
  'bhs-modell',
  'ablauf',
  'investition',
  'sonstiges',
];

function mapFaq(item: any): Faq {
  return {
    id: item.id,
    question: item.frage || '',
    answer: item.antwort || '',
    category: (item.kategorie || 'sonstiges') as FaqCategory,
    homeOrder: typeof item.reihenfolgeStartseite === 'number' ? item.reihenfolgeStartseite : 0,
  };
}

/**
 * Fetch the curated FAQ teaser entries for the homepage section.
 * Sorted ascending by `reihenfolgeStartseite`.
 */
export async function fetchHomeFaqs(limit = 6): Promise<Faq[]> {
  try {
    const endpoint =
      `/faqs?filters[aufStartseite][$eq]=true&sort=reihenfolgeStartseite:asc` +
      `&pagination[pageSize]=${limit}`;
    const response = await fetchStrapi<any>(endpoint);
    if (!response?.data) return [];
    return response.data.map(mapFaq);
  } catch (err) {
    console.error('Error fetching home FAQs from Strapi:', err);
    return [];
  }
}

/**
 * Fetch all FAQ entries for the FAQ page.
 * Sorted by category then by within-category order.
 */
export async function fetchAllFaqs(): Promise<Faq[]> {
  try {
    const endpoint = `/faqs?sort[0]=kategorie:asc&sort[1]=reihenfolge:asc&pagination[pageSize]=200`;
    const response = await fetchStrapi<any>(endpoint);
    if (!response?.data) return [];
    return response.data.map(mapFaq);
  } catch (err) {
    console.error('Error fetching all FAQs from Strapi:', err);
    return [];
  }
}

export async function fetchFaqSeite(): Promise<FaqSeite | null> {
  try {
    const response = await fetchStrapi<any>('/faq-seite');
    if (!response?.data) return null;
    const data = response.data;
    return {
      badgeText: data.badgeText || 'Häufige Fragen',
      heading: data.ueberschrift || 'Klarheit, bevor Sie buchen',
      subtitle: data.untertitel || '',
      buttonText: data.buttonText || 'Alle Fragen ansehen',
      buttonLink: data.buttonLink || '/faq',
      pageTitle: data.seitenTitel || 'Häufig gestellte Fragen',
      pageSubheading: data.seitenUnterueberschrift || '',
      seoDescription: data.seoBeschreibung || '',
      noEntriesText: data.keineEintraegeText || 'Aktuell sind keine Fragen hinterlegt.',
    };
  } catch (err) {
    console.error('Error fetching faq-seite from Strapi:', err);
    return null;
  }
}
